// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getProfile } from "../redux/slices/authSlice";
import { getWallet } from "../redux/slices/walletSlice";
import {
  getTransactions,
  getTransactionStatistics,
} from "../redux/slices/transactionSlice";
import { getKycStatus } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  TrendingUp,
  Wallet as WalletIcon,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  FileText,
  AlertCircle,
  Activity,
  Sparkles,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatFiat } from "../utils/currency";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wallet } = useSelector((state: RootState) => state.wallet);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions,
  );
  const { kyc } = useSelector((state: RootState) => state.kyc);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(getProfile() as any),
          dispatch(getWallet() as any),
          dispatch(getTransactions() as any),
          dispatch(getTransactionStatistics() as any),
          dispatch(getKycStatus() as any),
        ]);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]);

  const getVerificationStatus = (level: number) => {
    switch (level) {
      case 0:
        return {
          text: "Unverified",
          color: "text-red-500",
          icon: <XCircle className="h-4 w-4" />,
        };
      case 1:
        return {
          text: "Email Verified",
          color: "text-yellow-500",
          icon: <Clock className="h-4 w-4" />,
        };
      case 2:
        return {
          text: "KYC Submitted",
          color: "text-blue-500",
          icon: <FileText className="h-4 w-4" />,
        };
      case 3:
        return {
          text: "KYC Approved",
          color: "text-green-500",
          icon: <CheckCircle className="h-4 w-4" />,
        };
      default:
        return {
          text: "Unknown",
          color: "text-gray-500",
          icon: <AlertCircle className="h-4 w-4" />,
        };
    }
  };

  const getKycStatusText = () => {
    if (!kyc || !kyc.status) return "Not Submitted";
    return kyc.status.charAt(0).toUpperCase() + kyc.status.slice(1);
  };

  // Get recent transactions (deposits and withdrawals)
  const recentTransactions = transactions.slice(0, 5);

  // Calculate stats
  const totalDeposits = transactions
    .filter((t) => t.type === "deposit" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalWithdrawals = transactions
    .filter((t) => t.type === "withdrawal" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const pendingTransactions = transactions.filter(
    (t) => t.status === "pending",
  ).length;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading dashboard data...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Welcome back, {user?.first_name}!
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Here's what's happening with your account today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Balance Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                <WalletIcon className="h-5 w-5 text-primary" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
              >
                Active
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Balance
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(user?.balance || 0)}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                +2.5% from last month
              </span>
            </div>
          </div>

          {/* Deposits Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <ArrowDownRight className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Deposits
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(totalDeposits)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {transactions.filter((t) => t.type === "deposit").length}{" "}
              transactions
            </p>
          </div>

          {/* Withdrawals Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-red-100/50 to-red-50/50 dark:from-red-900/20 dark:to-red-800/10">
                <ArrowUpRight className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Withdrawals
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(totalWithdrawals)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {transactions.filter((t) => t.type === "withdrawal").length}{" "}
              transactions
            </p>
          </div>

          {/* Verification Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Verification Level
            </p>
            <div className="flex items-center mt-1">
              {getVerificationStatus(user?.verification_level || 0).icon}
              <p
                className={`text-xl font-bold ml-2 ${getVerificationStatus(user?.verification_level || 0).color}`}
              >
                Level {user?.verification_level}
              </p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {getVerificationStatus(user?.verification_level || 0).text}
            </p>
          </div>
        </div>

        {/* Recent Activity & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          {/* Recent Transactions */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                Recent Transactions
              </h3>
              <Link
                to="/transactions"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                View All
              </Link>
            </div>

            <div className="space-y-3">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-300 group"
                  >
                    <div className="flex items-center">
                      <div
                        className={`p-2 rounded-lg mr-3 transition-transform group-hover:scale-110 ${
                          transaction.type === "deposit"
                            ? "bg-green-100/50 dark:bg-green-900/20"
                            : transaction.type === "withdrawal"
                              ? "bg-red-100/50 dark:bg-red-900/20"
                              : "bg-purple-100/50 dark:bg-purple-900/20"
                        }`}
                      >
                        {transaction.type === "deposit" ? (
                          <ArrowDownRight className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : transaction.type === "withdrawal" ? (
                          <ArrowUpRight className="h-4 w-4 text-red-600 dark:text-red-400" />
                        ) : (
                          <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {transaction.type}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            transaction.created_at,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-medium ${
                          transaction.type === "deposit"
                            ? "text-green-600 dark:text-green-400"
                            : transaction.type === "withdrawal"
                              ? "text-red-600 dark:text-red-400"
                              : "text-purple-600 dark:text-purple-400"
                        }`}
                      >
                        {transaction.type === "deposit" ? "+" : "-"}
                        {formatFiat(transaction.amount)}
                      </p>
                      <span
                        className={`${GlobalStyles.badge} ${
                          transaction.status === "confirmed"
                            ? GlobalStyles.badgeSuccess
                            : transaction.status === "pending"
                              ? GlobalStyles.badgeWarning
                              : GlobalStyles.badgeError
                        } text-xs mt-1 px-2 py-0.5`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No transactions yet
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link
                to="/deposit"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group`}
              >
                <ArrowDownRight className="h-4 w-4 mr-2 transition-transform group-hover:-translate-y-0.5" />
                <span>Make Deposit</span>
              </Link>
              <Link
                to="/withdraw"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group`}
              >
                <ArrowUpRight className="h-4 w-4 mr-2 transition-transform group-hover:-translate-y-0.5" />
                <span>Request Withdrawal</span>
              </Link>
              {user?.verification_level === 1 && !kyc && (
                <Link
                  to="/profile"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group`}
                >
                  <Shield className="h-4 w-4 mr-2 transition-transform group-hover:-translate-y-0.5" />
                  <span>Complete KYC</span>
                </Link>
              )}
              <Link
                to="/recovery"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group`}
              >
                <Shield className="h-4 w-4 mr-2 transition-transform group-hover:-translate-y-0.5" />
                <span>Recovery Services</span>
              </Link>
              <Link
                to="/wallet"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} w-full justify-start group`}
              >
                <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover:-translate-y-0.5" />
                <span>Manage Wallet</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Wallet & KYC Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {/* Wallet Status */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
              Wallet Status
            </h3>
            {wallet ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {wallet.wallet_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connected on{" "}
                      {new Date(wallet.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
                  >
                    Connected
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Transactions
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      {transactions.length}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Network
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      Multi-chain
                    </p>
                  </div>
                </div>
                <Link
                  to="/wallet"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group`}
                >
                  <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-0.5" />
                  <span>Manage Wallet</span>
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                  <WalletIcon className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Connect your wallet to start transactions
                </p>
                <Link
                  to="/wallet"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group`}
                >
                  <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-0.5" />
                  <span>Connect Wallet</span>
                </Link>
              </div>
            )}
          </div>

          {/* KYC Status */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
              KYC Status
            </h3>
            {kyc ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Current Status
                  </span>
                  <span
                    className={`${GlobalStyles.badge} ${
                      kyc.status === "approved"
                        ? GlobalStyles.badgeSuccess
                        : kyc.status === "pending"
                          ? GlobalStyles.badgeWarning
                          : kyc.status === "rejected"
                            ? GlobalStyles.badgeError
                            : ""
                    } px-2 py-0.5`}
                  >
                    {getKycStatusText()}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Document Type:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                      {kyc.document_type?.replace("_", " ") || "N/A"}
                    </span>
                  </div>
                  {kyc.submitted_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Submitted:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(kyc.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {kyc.verified_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Verified:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(kyc.verified_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                {kyc.status === "pending" && (
                  <div
                    className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4 backdrop-blur-sm`}
                  >
                    <Clock className="h-5 w-5 flex-shrink-0" />
                    <p className="text-sm">
                      Your KYC verification is under review. This usually takes
                      24-48 hours.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                  <Shield className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Complete KYC verification to unlock all platform features
                </p>
                {user?.verification_level === 1 && (
                  <Link
                    to="/profile"
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group`}
                  >
                    <Shield className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-0.5" />
                    <span>Start KYC Verification</span>
                  </Link>
                )}
                {user?.verification_level === 0 && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    Please verify your email first
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Active Today
                </p>
                <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
                  {
                    transactions.filter(
                      (t) =>
                        new Date(t.created_at).toDateString() ===
                        new Date().toDateString(),
                    ).length
                  }
                </p>
              </div>
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pending
                </p>
                <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
                  {pendingTransactions}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-100/50 to-yellow-50/50 dark:from-yellow-900/20 dark:to-yellow-800/10">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </p>
                <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
                  {transactions.length > 0
                    ? `${Math.round(
                        (transactions.filter((t) => t.status === "confirmed")
                          .length /
                          transactions.length) *
                          100,
                      )}%`
                    : "0%"}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
